import React, { useState, useEffect } from "react";
import * as arrow from "apache-arrow";
import { Box, Theme, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CompactTableTextField from "../components/transformed_custom_view/viewer/CompactTableTextField";

import dayjs from "./dayjs";
import { shortFormatDateString } from "./date_format";
import {
  buildMUIColumns,
  makeGetRowFromArrowTableWithIndices,
  getSortedRowIndices,
  getRowFromArrowTable,
  calculateColumnTotals,
  getSegmentationFields,
  getAggregateField,
  processTableData,
  createCombinedTableMetadata,
  createGetRowFunction,
  createDemandWalkColumns,
  calculateColumnWidth,
  formatCellValue,
} from "./mui_table_utils";
import {
  getSupplyCellBackgroundColor,
  getWOSMeasureKey,
} from "./supply_color_coding";

// --- FLAT TABLE ---
export function getFlatTableData({
  arrowTable,
  columns,
  sortConfig = [],
  onSort,
  columnSchema,
  setSortConfig,
  firstMount,
}: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  sortConfig?: Array<{ key: string; direction: "asc" | "desc" }>;
  onSort?: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  setSortConfig?: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  firstMount?: React.MutableRefObject<boolean>;
}) {
  const numRows = arrowTable.numRows;

  // Use direct Arrow table sorting - no array conversion needed
  let visibleRows = Array.from({ length: numRows }, (_, i) => i);
  if (sortConfig && sortConfig.length > 0) {
    visibleRows = getSortedRowIndices(arrowTable, columns, sortConfig);
  }

  const muiColumns = buildMUIColumns({
    columns,
    sortConfig,
    onSort,
    columnSchema,
  });
  const getRow = makeGetRowFromArrowTableWithIndices(
    arrowTable,
    columns,
    visibleRows
  );

  // Calculate totals using the reusable function
  const columnTotals = calculateColumnTotals({
    arrowTable,
    columns: muiColumns,
    hasTableId: false,
  });

  return {
    columns: muiColumns,
    getRow,
    rowCount: visibleRows.length,
    arrowTable,
    columnTotals,
  };
}

// --- PIVOT TABLE ---
export function getPivotTableData({
  arrowTable,
  columns,
  viewParams,
  sortConfig = [],
  onSort,
  columnSchema,
  setSortConfig,
  firstMount,
}: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  viewParams: any;
  sortConfig?: Array<{ key: string; direction: "asc" | "desc" }>;
  onSort?: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  setSortConfig?: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  firstMount?: React.MutableRefObject<boolean>;
}) {
  const segmentKeys =
    viewParams.segments && viewParams.segments.length > 0
      ? [...viewParams.segments]
      : ["segment"];
  const dateKey = viewParams.dateHierarchy;
  const measureKey = viewParams.measure || "quantity";

  // Set initial sort config if empty and firstMount is provided
  if (firstMount?.current && setSortConfig) {
    const initialSortConfig = viewParams.segments.map(
      (segment: string, index: number) => ({
        key: segment,
        direction: "asc" as const,
        index,
      })
    );
    setSortConfig(initialSortConfig);
    firstMount.current = false; // Set to false immediately after setting sort config

    // Return empty table so that initial sortConfig is set
    return {
      columns: [],
      getRow: (rowIndex: number) => ({
        type: "detail" as const,
        dateValues: {},
        segmentationRank: 0,
      }),
      rowCount: 0,
      arrowTable: arrow.tableFromArrays({}),
      columnTotals: undefined,
    };
  }

  // Check if no segmentation is applied
  const noSegmentationCondition =
    !viewParams.segments || viewParams.segments.length === 0;

  if (noSegmentationCondition) {
    // Look for a total row in the data
    // Since there's a 'TOTAL' column, look for a row where the 'TOTAL' column has a value
    let totalRowIndex = -1;

    for (let i = 0; i < arrowTable.numRows; i++) {
      // Check if the 'TOTAL' column has a value
      const totalVector = arrowTable.getChild("TOTAL");
      if (totalVector) {
        const totalValue = totalVector.get(i);
        if (
          totalValue !== null &&
          totalValue !== undefined &&
          totalValue !== ""
        ) {
          totalRowIndex = i;
          break;
        }
      }
    }

    // If we found a total row, create a pivot-like view
    if (totalRowIndex >= 0) {
      // Get unique dates from the date hierarchy column using Arrow vectors directly
      const dateVector = arrowTable.getChild(dateKey);
      if (dateVector) {
        const dateSet = new Set<string>();
        for (let i = 0; i < dateVector.length; i++) {
          const dateValue = dateVector.get(i);
          if (dateValue != null) {
            dateSet.add(String(dateValue));
          }
        }
        const uniqueDates = Array.from(dateSet).sort();

        // Create columns for the total view
        const muiColumns = [
          {
            key: "total",
            label: " ",
            width: 120,
            sortable: false,
            sticky: true,
          },
          ...uniqueDates.map((date) => ({
            key: date,
            width: 100,
            headerType: new arrow.Timestamp(arrow.TimeUnit.MILLISECOND),
            sortable: false,
            sortDirection: sortConfig.find((s) => s.key === date)?.direction,
          })),
          {
            key: "TOTAL",
            label: "total",
            width: 120,
            sortable: false,
            sortDirection: sortConfig.find((s) => s.key === "TOTAL")?.direction,
          },
        ];

        // Create getRow function for the single total row
        const getRow = (rowIndex: number) => {
          if (rowIndex !== 0) {
            return {
              type: "detail" as const,
              dateValues: {},
              segmentationRank: 0,
            };
          }

          const row: Record<string, any> = { total: "Total" };

          // Get values for each date from the total row
          let rowTotal = 0;
          uniqueDates.forEach((date) => {
            const dateVector = arrowTable.getChild(dateKey);
            const measureVector = arrowTable.getChild(measureKey);

            if (dateVector && measureVector) {
              // Find the value for this date in the total row
              for (let i = 0; i < arrowTable.numRows; i++) {
                const currentDate = String(dateVector.get(i));
                if (currentDate === date) {
                  // Check if this is the total row by checking the TOTAL column
                  const totalVector = arrowTable.getChild("TOTAL");
                  if (totalVector) {
                    const totalValue = totalVector.get(i);
                    if (
                      totalValue !== null &&
                      totalValue !== undefined &&
                      totalValue !== ""
                    ) {
                      // Use the measure value for this date, not the date itself
                      const measureValue = measureVector.get(i);
                      row[date] = measureValue;

                      // Add to row total
                      if (
                        measureValue !== null &&
                        measureValue !== undefined &&
                        !isNaN(Number(measureValue))
                      ) {
                        rowTotal += Number(measureValue);
                      }
                      break;
                    }
                  }
                }
              }
            }
          });

          // Add the row total
          row["TOTAL"] = rowTotal;
          return row;
        };

        const result = {
          columns: muiColumns,
          getRow,
          rowCount: 1, // Only one row (the total row)
          arrowTable,
          columnTotals: undefined,
        };

        return result;
      }
    }
  }

  // 1. Get Arrow vectors directly (no array conversion)
  const segmentVectors = segmentKeys.map((key) => arrowTable.getChild(key));
  const dateVector = arrowTable.getChild(dateKey);
  const measureVector = arrowTable.getChild(measureKey);

  if (!segmentVectors.every((v) => v) || !dateVector || !measureVector) {
    return;
  }

  const numRows = arrowTable.numRows;

  // 2. Get unique values directly from vectors (no array conversion)
  const segmentComboMap = new Map<string, string[]>();
  for (let i = 0; i < numRows; i++) {
    const segmentValues = segmentVectors.map((vec) => vec!.get(i));
    const comboKey = segmentValues.join("||");
    if (!segmentComboMap.has(comboKey)) {
      segmentComboMap.set(comboKey, segmentValues);
    }
  }
  const uniqueSegmentCombos = Array.from(segmentComboMap.entries());

  const dateValueSet = new Set<any>();
  for (let i = 0; i < numRows; i++) {
    const dateValue = dateVector.get(i);
    if (dateValue !== undefined) {
      dateValueSet.add(dateValue);
    }
  }
  const uniqueDates = Array.from(dateValueSet).sort(
    (a, b) => Number(a) - Number(b)
  );

  // 3. Build aggregation map using direct vector access
  const aggregationMap = new Map<string, Map<any, number>>();

  // Initialize aggregation map
  for (const [comboKey] of uniqueSegmentCombos) {
    aggregationMap.set(comboKey, new Map());
    for (const date of uniqueDates) {
      aggregationMap.get(comboKey)!.set(date, 0);
    }
  }

  // 4. Stream through vectors once to build aggregations
  for (let i = 0; i < numRows; i++) {
    // Get values directly from vectors
    const segmentValues = segmentVectors.map((vec) => vec!.get(i));
    const comboKey = segmentValues.join("||");
    const dateValue = dateVector.get(i);
    const measureValue = Number(measureVector.get(i)) || 0;

    // Aggregate directly into the map
    if (aggregationMap.has(comboKey)) {
      const dateMap = aggregationMap.get(comboKey)!;
      if (dateMap.has(dateValue)) {
        dateMap.set(dateValue, dateMap.get(dateValue)! + measureValue);
      }
    }
  }

  // 5. Build matrix data using the aggregation map (minimal data creation)
  const matrixData: Record<string, any[]> = {};

  // Initialize columns
  segmentKeys.forEach((key) => {
    matrixData[key] = [];
  });
  uniqueDates.forEach((date) => {
    matrixData[String(date)] = [];
  });
  matrixData["TOTAL"] = [];

  // 6. Populate matrix data from aggregation map
  for (const [comboKey, combo] of uniqueSegmentCombos) {
    // Add segment values
    segmentKeys.forEach((key, i) => {
      matrixData[key].push(combo[i]);
    });

    // Add aggregated values and calculate total
    let total = 0;
    const dateMap = aggregationMap.get(comboKey)!;
    uniqueDates.forEach((date) => {
      const value = dateMap.get(date) || 0;
      matrixData[String(date)].push(value);
      total += value;
    });

    matrixData["TOTAL"].push(total);
  }

  // 7. Build Arrow Table from the matrix data (only final result as Arrow)
  const arrowPivoted = arrow.tableFromArrays(matrixData);

  // 8. Build columns: segment keys, date columns, TOTAL (JS array for MUI)
  const muiColumns = [
    ...segmentKeys.map((key, idx) => {
      // Calculate width for segmentation columns based on longest unique value
      const width = calculateColumnWidth(key, columnSchema);

      return {
        key,
        cellType: columns.find((c) => c.name === key)?.type || new arrow.Utf8(),
        headerType: new arrow.Utf8(),
        sticky: true,
        width,
        sortable: !!onSort,
        onSort: onSort
          ? (e: React.MouseEvent<HTMLTableCellElement>) => onSort(key, e)
          : undefined,
        sortDirection: sortConfig.find((s) => s.key === key)?.direction,
      };
    }),
    ...uniqueDates.map((dj) => ({
      key: String(dj),
      cellType: new arrow.Int64(),
      headerType: new arrow.Timestamp(arrow.TimeUnit.MILLISECOND),
      sortable: !!onSort,
      onSort: onSort
        ? (e: React.MouseEvent<HTMLTableCellElement>) => onSort(String(dj), e)
        : undefined,
      sortDirection: sortConfig.find((s) => s.key === String(dj))?.direction,
    })),
    {
      key: "TOTAL",
      sortable: !!onSort,
      cellType: new arrow.Int64(),
      headerType: new arrow.Utf8(),
      onSort: onSort
        ? (e: React.MouseEvent<HTMLTableCellElement>) => onSort("TOTAL", e)
        : undefined,
      sortDirection: sortConfig.find((s) => s.key === "TOTAL")?.direction,
    },
  ];

  // 9. Arrow-native sorting
  let visibleRows = Array.from({ length: arrowPivoted.numRows }, (_, i) => i);
  if (sortConfig && sortConfig.length > 0) {
    visibleRows = getSortedRowIndices(
      arrowPivoted,
      arrowPivoted.schema.fields,
      sortConfig
    );
  }

  // 10. Arrow-to-JS conversion function (only for visible rows)
  const getRow = (rowIndex: number) => {
    const actualRowIndex = visibleRows[rowIndex];
    return getRowFromArrowTable(
      arrowPivoted,
      arrowPivoted.schema.fields,
      actualRowIndex
    );
  };

  // Debug logs

  // Calculate totals using the reusable function
  const columnTotals = calculateColumnTotals({
    arrowTable: arrowPivoted,
    columns: muiColumns,
    hasTableId: false,
  });

  return {
    columns: muiColumns,
    getRow,
    rowCount: visibleRows.length,
    arrowTable: arrowPivoted,
    columnTotals,
  };
}

// --- FLAT TABLE ---
export function getTableData({
  arrowTable,
  columns,
  sortConfig = [],
  onSort,
  firstMount,
}: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  sortConfig?: Array<{ key: string; direction: "asc" | "desc" }>;
  onSort?: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
  firstMount?: React.MutableRefObject<boolean>;
}) {
  // Build MUI columns from Arrow columns
  const muiColumns = columns.map((column) => {
    const width = calculateColumnWidth(column.name, undefined);

    return {
      key: column.name,
      cellType: column.type,
      headerType: new arrow.Utf8(),
      width,
      sortable: !!onSort,
      onSort: onSort
        ? (e: React.MouseEvent<HTMLTableCellElement>) => onSort(column.name, e)
        : undefined,
      sortDirection: sortConfig.find((s) => s.key === column.name)?.direction,
    };
  });

  // Arrow-native sorting
  let visibleRows = Array.from({ length: arrowTable.numRows }, (_, i) => i);
  if (sortConfig && sortConfig.length > 0) {
    visibleRows = getSortedRowIndices(arrowTable, columns, sortConfig);
  }

  // Row access function with sorting
  const getRow = (rowIndex: number) => {
    const actualRowIndex = visibleRows[rowIndex];
    return getRowFromArrowTable(arrowTable, columns, actualRowIndex);
  };

  // Calculate totals using the reusable function
  const columnTotals = calculateColumnTotals({
    arrowTable,
    columns: muiColumns,
    hasTableId: false,
  });

  return {
    columns: muiColumns,
    getRow,
    rowCount: visibleRows.length,
    arrowTable,
    columnTotals,
  };
}

// --- COMPARISON TABLE ---
export function getComparisonTableData({
  arrowTable,
  columns,
  viewParams,
  sortConfig = [],
  onSort,
  columnSchema,
  setSortConfig,
  firstMount,
  expandedGroups,
  setExpandedGroups,
}: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  viewParams: any;
  sortConfig?: Array<{ key: string; direction: "asc" | "desc" }>;
  onSort?: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  setSortConfig?: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  firstMount?: React.MutableRefObject<boolean>;
  expandedGroups?: Set<string>;
  setExpandedGroups?: React.Dispatch<React.SetStateAction<Set<string>>>;
}) {
  const segmentKeys =
    viewParams.segments && viewParams.segments.length > 0
      ? [...viewParams.segments]
      : ["segment"];
  const dateKey = viewParams.dateHierarchy;
  const measureKey = viewParams.measure || "quantity";
  const desiredOrder = ["1st", "2nd", "delta (1st - 2nd)"];
  const numRows = arrowTable.numRows;

  // Set initial sort config if empty and firstMount is provided
  if (firstMount?.current && setSortConfig) {
    // For comparison tables, sort by TOTAL column descending
    // For other tables, sort by segments ascending
    const initialSortConfig = [
      {
        key: "TOTAL",
        direction: "desc" as const,
        index: 0,
      },
    ];

    setSortConfig(initialSortConfig);
    firstMount.current = false; // Set to false immediately after setting sort config

    // Return empty table so that initial sortConfig is set
    return {
      columns: [],
      getRow: (rowIndex: number) => ({
        type: "detail" as const,
        dateValues: {},
        segmentationRank: 0,
      }),
      rowCount: 0,
      arrowTable: arrow.tableFromArrays({}),
      columnTotals: undefined,
    };
  }

  // 1. Get Arrow vectors directly (no array conversion)
  const segmentVectors = segmentKeys.map((key) => arrowTable.getChild(key));
  const dateVector = arrowTable.getChild(dateKey);
  const measureVector = arrowTable.getChild(measureKey);
  const tableIdVector = arrowTable.getChild("table_id");

  if (!segmentVectors.every((v) => v) || !dateVector || !measureVector) {
    return;
  }

  // 2. Get unique values directly from vectors (no array conversion)
  const segmentComboSet = new Set<string>();
  const dateValueSet = new Set<any>();
  const tableIdSet = new Set<string>();

  // Stream through vectors once to collect unique combinations
  for (let i = 0; i < numRows; i++) {
    const segmentValues = segmentVectors
      .filter((_, idx) => segmentKeys[idx] !== "table_id")
      .map((vec) => vec!.get(i));
    const combo = segmentValues.join("||");
    segmentComboSet.add(combo);

    if (tableIdVector) {
      const tableId = tableIdVector.get(i);
      if (tableId) tableIdSet.add(String(tableId));
    }

    const dateValue = dateVector.get(i);
    if (dateValue !== undefined) {
      dateValueSet.add(dateValue);
    }
  }

  const segmentCombos = Array.from(segmentComboSet);
  const dateValues = Array.from(dateValueSet).sort(
    (a, b) => Number(a) - Number(b)
  );

  // 3. Build aggregation using direct vector access
  const valueMap = new Map<string, Map<string, Map<any, number>>>();

  // Initialize aggregation structure
  for (const tableId of desiredOrder) {
    valueMap.set(tableId, new Map());
    for (const combo of segmentCombos) {
      valueMap.get(tableId)!.set(combo, new Map());
      for (const date of dateValues) {
        valueMap.get(tableId)!.get(combo)!.set(date, 0);
      }
    }
  }

  // 4. Stream through vectors once to build aggregations
  for (let i = 0; i < numRows; i++) {
    const tableId = tableIdVector
      ? String(tableIdVector.get(i) || "unknown")
      : "unknown";
    const segmentValues = segmentVectors
      .filter((_, idx) => segmentKeys[idx] !== "table_id")
      .map((vec) => vec!.get(i));
    const combo = segmentValues.join("||");
    const date = dateVector.get(i);
    const value = Number(measureVector.get(i)) || 0;

    // Aggregate directly into the map
    if (valueMap.has(tableId) && valueMap.get(tableId)!.has(combo)) {
      const dateMap = valueMap.get(tableId)!.get(combo)!;
      if (dateMap.has(date)) {
        dateMap.set(date, dateMap.get(date)! + value);
      }
    }
  }

  // 5. Build matrix data using the aggregation map (minimal data creation)
  const matrixData: Record<string, any[]> = {};

  // Initialize columns
  matrixData["table_id"] = [];
  segmentKeys
    .filter((k) => k !== "table_id")
    .forEach((key) => {
      matrixData[key] = [];
    });
  dateValues.forEach((dj) => {
    matrixData[String(dj)] = [];
  });
  matrixData["TOTAL"] = [];

  // 6. Populate matrix data from aggregation map
  desiredOrder.forEach((tableId) => {
    segmentCombos.forEach((combo) => {
      const segVals = combo.split("||");
      matrixData["table_id"].push(tableId);
      segmentKeys
        .filter((k) => k !== "table_id")
        .forEach((key, i) => {
          matrixData[key].push(segVals[i]);
        });

      let total = 0;
      const dateMap = valueMap.get(tableId)?.get(combo);
      dateValues.forEach((dj) => {
        const v = dateMap?.get(dj) || 0;
        matrixData[String(dj)].push(v);
        total += v;
      });
      matrixData["TOTAL"].push(total);
    });
  });

  // 7. Build Arrow Table from the matrix data (only final result as Arrow)
  const arrowComparison = arrow.tableFromArrays(matrixData);

  // 8. Group by segment combinations for expand/collapse
  const groupMap = new Map<
    string,
    {
      groupKey: string;
      groupType: "delta" | "total";
      segmentValues: Record<string, any>;
      groupValues: Record<string, any>;
      secondRowIndices: number[];
      firstRowIndices: number[];
    }
  >();

  // Find delta rows and group them by segment combination
  for (let i = 0; i < arrowComparison.numRows; i++) {
    const tableId = String(arrowComparison.getChild("table_id")?.get(i) || "");
    if (tableId === "delta (1st - 2nd)") {
      const segmentValues: Record<string, any> = {};
      segmentKeys
        .filter((k) => k !== "table_id")
        .forEach((key) => {
          segmentValues[key] = arrowComparison.getChild(key)?.get(i);
        });

      const groupKey = Object.values(segmentValues).join("||");

      // Find corresponding 2nd and 1st rows for this segment combination
      const secondRowIndices: number[] = [];
      const firstRowIndices: number[] = [];

      for (let j = 0; j < arrowComparison.numRows; j++) {
        const rowTableId = String(
          arrowComparison.getChild("table_id")?.get(j) || ""
        );
        const rowSegmentValues: Record<string, any> = {};
        segmentKeys
          .filter((k) => k !== "table_id")
          .forEach((key) => {
            rowSegmentValues[key] = arrowComparison.getChild(key)?.get(j);
          });
        const rowGroupKey = Object.values(rowSegmentValues).join("||");

        if (rowGroupKey === groupKey) {
          if (rowTableId === "2nd") {
            secondRowIndices.push(j);
          } else if (rowTableId === "1st") {
            firstRowIndices.push(j);
          }
        }
      }

      // Store delta values
      const deltaValues: Record<string, any> = {};
      dateValues.forEach((dj) => {
        deltaValues[String(dj)] = arrowComparison.getChild(String(dj))?.get(i);
      });
      deltaValues["TOTAL"] = arrowComparison.getChild("TOTAL")?.get(i);

      groupMap.set(groupKey, {
        groupKey,
        groupType: "delta",
        segmentValues,
        groupValues: deltaValues,
        secondRowIndices,
        firstRowIndices,
      });
    }
  }

  // Special handling for the 3 total header rows
  const totalHeaderGroups = new Map<
    string,
    {
      groupKey: string;
      groupType: "total";
      segmentValues: Record<string, any>;
      groupValues: Record<string, any>;
      detailRowIndices: number[];
    }
  >();
  // Find the 3 total header rows (rows with "Total" in channel column and specific table_id values)
  for (let i = 0; i < arrowComparison.numRows; i++) {
    const tableId = String(arrowComparison.getChild("table_id")?.get(i) || "");
    const channelValue = arrowComparison.getChild("channel")?.get(i);
    const productCategoryValue = arrowComparison
      .getChild("product category")
      ?.get(i);

    // Check if this is a total header row by looking for "Total" in channel or product category column
    const hasTotalInChannel =
      String(channelValue || "").toLowerCase() === "total";
    const hasTotalInProductCategory =
      String(productCategoryValue || "").toLowerCase() === "total";
    const isTotalHeaderRow =
      (hasTotalInChannel || hasTotalInProductCategory) &&
      (tableId === "2nd" ||
        tableId === "1st" ||
        tableId === "delta (1st - 2nd)");

    if (isTotalHeaderRow) {
      const segmentValues: Record<string, any> = {};
      segmentKeys
        .filter((k) => k !== "table_id")
        .forEach((key) => {
          segmentValues[key] = arrowComparison.getChild(key)?.get(i);
        });

      const groupKey = `${tableId}||${Object.values(segmentValues).join("||")}`;

      // Find all detail rows that belong to this total group
      const detailRowIndices: number[] = [];

      for (let j = 0; j < arrowComparison.numRows; j++) {
        const rowTableId = String(
          arrowComparison.getChild("table_id")?.get(j) || ""
        );
        if (rowTableId === tableId) {
          // Check if this is a detail row (has specific values, not "Total")
          const rowChannelValue = arrowComparison.getChild("channel")?.get(j);
          const rowProductCategoryValue = arrowComparison
            .getChild("product category")
            ?.get(j);
          const isDetailRow =
            String(rowChannelValue || "").toLowerCase() !== "total" &&
            String(rowProductCategoryValue || "").toLowerCase() !== "total";

          if (isDetailRow) {
            detailRowIndices.push(j);
          }
        }
      }

      // Store total values - these are the actual calculated values from the Arrow table
      const totalValues: Record<string, any> = {};
      dateValues.forEach((dj) => {
        const value = arrowComparison.getChild(String(dj))?.get(i);
        totalValues[String(dj)] = value;
      });

      const totalValue = arrowComparison.getChild("TOTAL")?.get(i);
      totalValues["TOTAL"] = totalValue;

      totalHeaderGroups.set(groupKey, {
        groupKey,
        groupType: "total",
        segmentValues,
        groupValues: totalValues,
        detailRowIndices,
      });
    }
  }

  // 9. Sort groups
  const sortedGroups = Array.from(groupMap.values()).sort((a, b) => {
    // Apply sort config if available
    for (const sort of sortConfig) {
      const aValue = a.groupValues[sort.key] ?? a.segmentValues[sort.key] ?? 0;
      const bValue = b.groupValues[sort.key] ?? b.segmentValues[sort.key] ?? 0;

      if (typeof aValue === "number" && typeof bValue === "number") {
        // Sort by absolute values, respecting the direction
        const aAbs = Math.abs(aValue);
        const bAbs = Math.abs(bValue);
        const comparison = aAbs - bAbs; // Natural order (smallest to largest)

        if (comparison !== 0) {
          return sort.direction === "asc" ? comparison : -comparison;
        }
      } else {
        const aStr = String(aValue);
        const bStr = String(bValue);
        const comparison = aStr.localeCompare(bStr);
        if (comparison !== 0) {
          return sort.direction === "asc" ? comparison : -comparison;
        }
      }
    }
    return 0;
  });

  // Sort total header groups
  const sortedTotalGroups = Array.from(totalHeaderGroups.values()).sort(
    (a, b) => {
      // Sort by table_id order: "2nd", "1st", "delta (1st - 2nd)"
      const order = { "1st": 0, "2nd": 1, "delta (1st - 2nd)": 2 };
      const aTableId = a.groupKey.split("||")[0];
      const bTableId = b.groupKey.split("||")[0];
      return (
        (order[aTableId as keyof typeof order] || 0) -
        (order[bTableId as keyof typeof order] || 0)
      );
    }
  );

  // 10. Calculate row counts and positions
  const groupInfos: Array<{
    groupKey: string;
    groupType: "delta" | "total";
    segmentValues: Record<string, any>;
    groupValues: Record<string, any>;
    secondRowIndices?: number[];
    firstRowIndices?: number[];
    detailRowIndices?: number[];
    startRowIndex: number;
  }> = [];
  let totalRowCount = 0;

  // Add total header groups first
  sortedTotalGroups.forEach((groupData) => {
    const isExpanded =
      expandedGroups?.has("*") ||
      expandedGroups?.has(groupData.groupKey) ||
      false;
    const detailCount = isExpanded ? groupData.detailRowIndices.length : 0;

    groupInfos.push({
      ...groupData,
      startRowIndex: totalRowCount,
    });

    totalRowCount += 1 + detailCount; // 1 for group row + detail rows
  });

  // Then add delta groups
  sortedGroups.forEach((groupData) => {
    const isExpanded =
      expandedGroups?.has("*") ||
      expandedGroups?.has(groupData.groupKey) ||
      false;
    const detailCount = isExpanded
      ? groupData.secondRowIndices.length + groupData.firstRowIndices.length
      : 0;

    groupInfos.push({
      ...groupData,
      startRowIndex: totalRowCount,
    });

    totalRowCount += 1 + detailCount; // 1 for group row + detail rows
  });

  // 11. Build columns with expand column
  const muiColumns = [
    {
      key: "expand",
      label: "",
      width: 40,
      align: "center" as const,
      isExpandColumn: true,
      sticky: true,
    },
    {
      key: "table_id",
      label: "run",
      cellType: new arrow.Utf8(),
      headerType: new arrow.Utf8(),
      width: 130,
      sticky: true,
    },
    ...segmentKeys
      .filter((k) => k !== "table_id")
      .map((key, idx) => {
        // Calculate width for segmentation columns based on longest unique value
        const width = calculateColumnWidth(key, columnSchema);

        return {
          key,
          cellType:
            columns.find((c) => c.name === key)?.type || new arrow.Utf8(),
          headerType: new arrow.Utf8(),
          sticky: true,
          width,
          sortable: !!onSort,
          onSort: onSort
            ? (e: React.MouseEvent<HTMLTableCellElement>) => onSort(key, e)
            : undefined,
          sortDirection: sortConfig.find((s) => s.key === key)?.direction,
        };
      }),
    ...dateValues.map((dj) => ({
      key: String(dj),
      cellType: new arrow.Int64(),
      headerType: new arrow.Timestamp(arrow.TimeUnit.MILLISECOND),
      sortable: !!onSort,
      isComparison: true,
      onSort: onSort
        ? (e: React.MouseEvent<HTMLTableCellElement>) => onSort(String(dj), e)
        : undefined,
      sortDirection: sortConfig.find((s) => s.key === String(dj))?.direction,
    })),
    {
      key: "TOTAL",
      sortable: !!onSort,
      cellType: new arrow.Int64(),
      headerType: new arrow.Utf8(),
      isComparison: true,
      onSort: onSort
        ? (e: React.MouseEvent<HTMLTableCellElement>) => onSort("TOTAL", e)
        : undefined,
      sortDirection: sortConfig.find((s) => s.key === "TOTAL")?.direction,
    },
  ];

  // 12. Lazy row access function
  const getRow = (rowIndex: number): Record<string, any> => {
    if (rowIndex >= totalRowCount) {
      return {};
    }

    // Find which group this row belongs to
    let groupInfo: (typeof groupInfos)[0] | null = null;
    let localRowIndex = 0;

    for (const info of groupInfos) {
      const isExpanded =
        expandedGroups?.has("*") || expandedGroups?.has(info.groupKey) || false;
      let detailCount = 0;

      if (info.groupType === "total") {
        detailCount = isExpanded ? info.detailRowIndices?.length || 0 : 0;
      } else {
        detailCount = isExpanded
          ? (info.secondRowIndices?.length || 0) +
            (info.firstRowIndices?.length || 0)
          : 0;
      }

      const groupEndIndex = info.startRowIndex + 1 + detailCount;

      if (rowIndex >= info.startRowIndex && rowIndex < groupEndIndex) {
        groupInfo = info;
        localRowIndex = rowIndex - info.startRowIndex;
        break;
      }
    }

    if (!groupInfo) {
      return {};
    }

    // Row 0 is always the group row
    if (localRowIndex === 0) {
      const rowData: Record<string, any> = {
        type: "group",
        groupKey: groupInfo.groupKey,
        isExpanded:
          expandedGroups?.has("*") ||
          expandedGroups?.has(groupInfo.groupKey) ||
          false,
        groupSize:
          groupInfo.groupType === "total"
            ? groupInfo.detailRowIndices?.length || 0
            : (groupInfo.secondRowIndices?.length || 0) +
              (groupInfo.firstRowIndices?.length || 0),
        table_id:
          groupInfo.groupType === "total"
            ? groupInfo.groupKey.split("||")[0]
            : "delta (1st - 2nd)",
      };

      // Add segment values
      Object.entries(groupInfo.segmentValues).forEach(([key, value]) => {
        rowData[key] = value;
      });

      // Add group values
      Object.entries(groupInfo.groupValues).forEach(([key, value]) => {
        rowData[key] = value;
      });

      return rowData;
    }

    // Otherwise it's a detail row (only if expanded)
    const detailIndex = localRowIndex - 1;

    if (groupInfo.groupType === "total") {
      // Handle total group detail rows
      const totalDetailRows = groupInfo.detailRowIndices?.length || 0;
      if (detailIndex >= totalDetailRows) {
        return {};
      }
      const actualRowIndex = groupInfo.detailRowIndices![detailIndex];

      // Convert Arrow row to JS
      const rowData: Record<string, any> = {
        type: "detail",
        groupKey: groupInfo.groupKey,
      };

      // Get individual row values for all columns
      muiColumns.forEach((col) => {
        if (col.key === "expand") return;

        const vector = arrowComparison.getChild(col.key);
        if (!vector) return;

        const value = vector.get(actualRowIndex);
        if (typeof value === "number") {
          rowData[col.key] = value;
        } else if (typeof value === "bigint") {
          rowData[col.key] = Number(value);
        } else {
          rowData[col.key] = value || "";
        }
      });

      return rowData;
    } else {
      // Handle delta group detail rows (original logic)
      const totalDetailRows =
        (groupInfo.secondRowIndices?.length || 0) +
        (groupInfo.firstRowIndices?.length || 0);
      if (detailIndex >= totalDetailRows) {
        return {};
      }

      // Determine if this is a 1st or 2nd row (1st rows first, then 2nd rows)
      const isFirstRow = detailIndex < (groupInfo.firstRowIndices?.length || 0);
      const actualRowIndex = isFirstRow
        ? groupInfo.firstRowIndices![detailIndex]
        : groupInfo.secondRowIndices![
            detailIndex - (groupInfo.firstRowIndices?.length || 0)
          ];

      // Convert Arrow row to JS
      const rowData: Record<string, any> = {
        type: "detail",
        groupKey: groupInfo.groupKey,
      };

      // Get individual row values for all columns
      muiColumns.forEach((col) => {
        if (col.key === "expand") return;

        const vector = arrowComparison.getChild(col.key);
        if (!vector) return;

        const value = vector.get(actualRowIndex);
        if (typeof value === "number") {
          rowData[col.key] = value;
        } else if (typeof value === "bigint") {
          rowData[col.key] = Number(value);
        } else {
          rowData[col.key] = value || "";
        }
      });

      return rowData;
    }
  };

  return {
    columns: muiColumns,
    getRow,
    rowCount: totalRowCount,
    arrowTable: arrowComparison,
    columnTotals: calculateColumnTotals({
      arrowTable: arrowComparison,
      columns: muiColumns,
      hasTableId: true,
    }),
  };
}

// --- DEMAND WALK SINGLE TABLE ---
export function getDemandWalkSingleTableData(args: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  viewParams: any;
  demandWalkConfig?: {
    plannerOverrideInputs: Record<string, Record<string, string>>;
    finalForecastInputs: Record<string, Record<string, string>>;
    yoyPercentageInputs: Record<string, Record<string, string>>;
    setPlannerOverrideInputs: React.Dispatch<
      React.SetStateAction<Record<string, Record<string, string>>>
    >;
    setFinalForecastInputs: React.Dispatch<
      React.SetStateAction<Record<string, Record<string, string>>>
    >;
    setYoYPercentageInputs: React.Dispatch<
      React.SetStateAction<Record<string, Record<string, string>>>
    >;
    editMode?: boolean;
  };
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  onSort: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
  theme: any;
  expandedGroups: Set<string>;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
  firstMount?: React.MutableRefObject<boolean>;
}) {
  const { arrowTable, columns, viewParams, demandWalkConfig, columnSchema } =
    args;

  // Handle the case where demandWalkConfig is not provided
  if (!demandWalkConfig) {
    return null;
  }

  // Get segmentation fields and aggregate field
  const segmentationFields = getSegmentationFields(viewParams, columns);
  const aggregateField = getAggregateField(viewParams);

  // Process table data using utilities
  const { tableData, monthNames, segmentationGroups } = processTableData(
    arrowTable,
    columns,
    segmentationFields
  );

  // Create combined table metadata
  const { sortedMetadata, combinedArrowTable } = createCombinedTableMetadata(
    tableData,
    segmentationGroups,
    segmentationFields
  );

  // Create getRow function with original Arrow table for direct vector access
  const getRow = createGetRowFunction(
    sortedMetadata,
    tableData,
    segmentationFields,
    monthNames,
    aggregateField,
    demandWalkConfig,
    arrowTable, // Pass original Arrow table for direct vector access
    columns // Pass original columns for direct vector access
  );

  // Create columns using utilities
  const muiColumns = createDemandWalkColumns({
    segmentationFields,
    monthNames,
    tableData,
    aggregateField,
    demandWalkConfig,
    getRow,
    columnSchema,
    originalArrowTable: arrowTable, // Pass original Arrow table for direct vector access
    originalColumns: columns, // Pass original columns for direct vector access
  });

  return {
    columns: muiColumns,
    getRow,
    rowCount: combinedArrowTable.numRows,
    arrowTable: combinedArrowTable,
    columnTotals: {}, // No totals for demand walk
  };
}

// --- SUPPLY WALK TABLE ---
export function getSupplyWalkTableData({
  arrowTable,
  columns,
  viewParams,
  columnSchema,
  theme,
  firstMount,
}: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  viewParams: any;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  theme: Theme;
  firstMount?: React.MutableRefObject<boolean>;
}) {
  if (
    !arrowTable ||
    !columns ||
    !viewParams?.segments ||
    !viewParams?.dateHierarchy
  ) {
    return null;
  }

  // Get unique dates efficiently using Arrow vectors directly
  const dateField = viewParams.dateHierarchy;
  const dateVector = arrowTable.getChild(dateField);
  if (!dateVector) {
    return null;
  }

  const dateSet = new Set<number>();
  for (let i = 0; i < dateVector.length; i++) {
    const dateValue = dateVector.get(i);
    if (dateValue != null) {
      dateSet.add(dayjs.utc(dateValue).valueOf());
    }
  }
  const allDates = Array.from(dateSet)
    .sort((a, b) => a - b)
    .map((ts) => dayjs.utc(ts));

  // Get all necessary vectors once
  const segmentVectors = viewParams.segments.map((segment: string) =>
    arrowTable.getChild(segment)
  );
  const measureVector = arrowTable.getChild("measure");
  const valueVector = arrowTable.getChild("value");
  const segmentationRankVector =
    arrowTable.getChild("segmentation_rank") ||
    arrowTable.getChild("segmentationRank");
  const measureRankVector =
    arrowTable.getChild("measure_rank") || arrowTable.getChild("metric_rank");

  if (
    !measureVector ||
    !valueVector ||
    segmentVectors.some((v: arrow.Vector | null) => !v)
  ) {
    return null;
  }

  // Build efficient lookup maps in one pass (O(n) upfront, O(1) lookup)
  const segMeasureToRows = new Map<string, number[]>();
  const segMeasureKeys: string[] = [];
  const segMeasureData = new Map<
    string,
    {
      segmentationKey: string;
      measure: string;
      segmentValues: string[];
      segmentationRank: number;
      measureRank: number;
    }
  >();

  // Single pass through Arrow data to build all lookup structures
  for (let i = 0; i < arrowTable.numRows; i++) {
    const segmentValues = segmentVectors.map((vector: arrow.Vector | null) =>
      String(vector!.get(i) || "")
    );
    const segmentationKey = segmentValues.join(" | ");
    const measure = String(measureVector.get(i) || "");
    const combinedKey = `${segmentationKey}||${measure}`;

    // Track arrow row indices for this segmentation+measure
    if (!segMeasureToRows.has(combinedKey)) {
      segMeasureToRows.set(combinedKey, []);
      segMeasureKeys.push(combinedKey);

      // Store metadata for this combination
      segMeasureData.set(combinedKey, {
        segmentationKey,
        measure,
        segmentValues,
        segmentationRank: Number(segmentationRankVector?.get(i) || 0),
        measureRank: Number(measureRankVector?.get(i) || 0),
      });
    }
    segMeasureToRows.get(combinedKey)!.push(i);
  }

  // Validate and fix segmentation rank consistency
  const segmentationRanks = new Map<string, number>();
  for (const [combinedKey, data] of segMeasureData.entries()) {
    const existingRank = segmentationRanks.get(data.segmentationKey);
    if (existingRank === undefined) {
      segmentationRanks.set(data.segmentationKey, data.segmentationRank);
    } else if (existingRank !== data.segmentationRank) {
      console.warn(
        `[SupplyWalk] Inconsistent segmentation rank for "${data.segmentationKey}": ${existingRank} vs ${data.segmentationRank}`
      );
      // Use the first encountered rank for consistency
      data.segmentationRank = existingRank;
    }
  }

  // Sort combinations by ranks (one-time sort)
  const sortedKeys = segMeasureKeys.sort((keyA, keyB) => {
    const dataA = segMeasureData.get(keyA)!;
    const dataB = segMeasureData.get(keyB)!;

    // Primary sort: segmentation rank
    if (dataA.segmentationRank !== dataB.segmentationRank) {
      return dataA.segmentationRank - dataB.segmentationRank;
    }

    // Secondary sort: segmentation string (ensures stable grouping)
    if (dataA.segmentationKey !== dataB.segmentationKey) {
      return dataA.segmentationKey.localeCompare(dataB.segmentationKey);
    }

    // Tertiary sort: measure rank within same segmentation
    return dataA.measureRank - dataB.measureRank;
  });

  // Create efficient getRow function with O(1) lookups
  const getRow = (rowIndex: number): Record<string, any> => {
    if (rowIndex >= sortedKeys.length) return {}; // Return empty object instead of null

    const metadata = segMeasureData.get(sortedKeys[rowIndex])!;
    const arrowRowIndices = segMeasureToRows.get(sortedKeys[rowIndex])!;

    // Build date values efficiently
    const dateValues: Record<string, any> = {};
    arrowRowIndices.forEach((arrowRowIndex) => {
      const dateValue = dayjs.utc(dateVector.get(arrowRowIndex)).valueOf();
      const value = valueVector.get(arrowRowIndex);
      dateValues[dateValue] = value;
    });

    // Create the base row data with export compatibility properties
    const rowData: Record<string, any> = {
      segmentationKey: metadata.segmentationKey,
      segmentValues: metadata.segmentValues,
      measure: metadata.measure,
      dateValues,
      segmentationRank: metadata.segmentationRank,
      measureRank: metadata.measureRank,
      _arrowRowIndices: arrowRowIndices,

      // Export compatibility properties to match the pattern expected by export logic
      // For supply walk, treat each row as a "detail" row since there's no grouping
      type: "detail",
      groupKey: metadata.segmentationKey,

      // Direct column access for export
      segmentation: metadata.segmentationKey,
    };

    // Add date column values directly on the row object for export
    allDates.forEach((date) => {
      const dateKey = `${date.valueOf()}`;
      rowData[dateKey] = dateValues[date.valueOf()] || "";
    });

    return rowData;
  };

  // Build MUI columns
  const muiColumns = [
    {
      key: "segmentation",
      cellType: new arrow.Utf8(),
      headerType: new arrow.Utf8(),
      sticky: true,
      width: calculateColumnWidth(viewParams.segments, columnSchema) || 120,
      renderCell: (row: any, rowIndex: number) => {
        // Determine if this is the first measure in the segmentation group
        const prevRow = rowIndex === 0 ? null : getRow(rowIndex - 1);
        const isFirstInGroup =
          rowIndex === 0 ||
          !prevRow ||
          prevRow.segmentationKey !== row.segmentationKey;

        // Check if there are no segmentations (empty or only separators)
        const hasNoSegmentations =
          !row.segmentationKey ||
          row.segmentationKey.trim() === "" ||
          row.segmentationKey.replace(/\s*\|\s*/g, "").trim() === "";

        // Display 'Total' when there are no segmentations and this is the first row
        const displayValue =
          hasNoSegmentations && rowIndex === 0
            ? "Total"
            : isFirstInGroup
              ? row.segmentationKey
              : "";

        return (
          <Box
            sx={{
              height: "40px",
              display: "flex",
              alignItems: "center",
              p: 1,
            }}
          >
            {displayValue}
          </Box>
        );
      },
    },
    {
      key: "measure",
      cellType: new arrow.Utf8(),
      headerType: new arrow.Utf8(),
      sticky: true,
      width: 200,
      renderCell: (row: any, rowIndex: number) => {
        return (
          <Box
            sx={{
              height: "40px",
              display: "flex",
              alignItems: "center",
              p: 1,
            }}
          >
            {row.measure}
          </Box>
        );
      },
    },
    ...allDates.map((date) => ({
      key: `${date.valueOf()}`,
      cellType: new arrow.Float64(),
      headerType: new arrow.Timestamp(arrow.TimeUnit.MILLISECOND),

      label: shortFormatDateString(date),
      renderCell: (row: any, rowIndex: number) => {
        const value = row.dateValues[date.valueOf()];
        const numericString = String(value ?? "").replace(/,/g, "");

        // Highlight purchase order to place and purchase order arrivals
        const isPOPlaced =
          row.measure.toLowerCase() === "purchase order to place";
        const isPOArrivals =
          row.measure.toLowerCase() === "purchase order arrivals";
        let highlight = false;
        if (
          (isPOPlaced &&
            numericString !== "" &&
            !isNaN(Number(numericString)) &&
            Number(numericString) > 0) ||
          isPOArrivals
        ) {
          highlight = true;
        }

        // Color code weeks of supply
        let cellColor = undefined;
        const isWOS =
          row.measure.toLowerCase() ===
            getWOSMeasureKey(
              "starting",
              viewParams?.dateHierarchy || ""
            ).toLowerCase() ||
          row.measure.toLowerCase() ===
            getWOSMeasureKey(
              "ending",
              viewParams?.dateHierarchy || ""
            ).toLowerCase() ||
          row.measure.toLowerCase().includes("target weeks of supply");

        // Skip coloring for target weeks of supply
        const isTargetWOS = row.measure
          .toLowerCase()
          .includes("target weeks of supply");

        if (
          isWOS &&
          !isTargetWOS &&
          numericString !== "" &&
          !isNaN(Number(numericString))
        ) {
          // Find target WOS within the same segmentation
          let targetWOS = null;
          const targetMeasureName = getWOSMeasureKey(
            "target",
            viewParams?.dateHierarchy || ""
          );

          // Find target measure in current segmentation by looking at nearby rows
          for (
            let i = Math.max(0, rowIndex - 10);
            i <= Math.min(sortedKeys.length - 1, rowIndex + 10);
            i++
          ) {
            const checkRow = getRow(i);
            if (
              checkRow &&
              checkRow.segmentationKey &&
              checkRow.segmentationKey === row.segmentationKey &&
              checkRow.measure &&
              checkRow.measure.toLowerCase() === targetMeasureName.toLowerCase()
            ) {
              const targetValRaw = checkRow.dateValues[date.valueOf()];
              if (
                targetValRaw !== undefined &&
                targetValRaw !== null &&
                targetValRaw !== ""
              ) {
                const targetString =
                  typeof targetValRaw === "string"
                    ? targetValRaw.replace(/,/g, "")
                    : String(targetValRaw);
                const parsedTarget = Number(targetString);
                if (!isNaN(parsedTarget)) {
                  targetWOS = parsedTarget;
                }
              }
              break;
            }
          }

          const currentValue = Number(numericString);

          if (targetWOS !== null && !isNaN(targetWOS)) {
            cellColor = getSupplyCellBackgroundColor({
              value: currentValue,
              targetValue: targetWOS,
              theme,
            });
          }
        }

        // Determine background color
        let backgroundColor = "white"; // Default white background
        if (highlight) {
          backgroundColor = theme.palette.atomic?.orange1 || "#fff3cd"; // Use atomic orange1 from theme
        } else if (cellColor) {
          backgroundColor = cellColor; // Use calculated color for weeks of supply
        }

        return (
          <Box
            sx={{
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end", // Right-align numerical values
              backgroundColor: backgroundColor,
              p: 1,
            }}
          >
            {value ?? ""}
          </Box>
        );
      },
    })),
  ];

  return {
    columns: muiColumns,
    getRow,
    rowCount: sortedKeys.length,
    arrowTable,
    columnTotals: {},
  };
}

// --- SUPPLY WALK EDIT TABLE (Copy for nested details) ---
export function getSupplyWalkEditTableData({
  arrowTable,
  columns,
  viewParams,
  columnSchema,
  theme,
  firstMount,
  parentEdit,
}: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  viewParams: any;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  theme: Theme;
  firstMount?: React.MutableRefObject<boolean>;
  parentEdit?: {
    quantity: number;
    orderPlacedDate: string;
    deliveryDate: string;
    approved?: boolean;
  } | null;
}) {
  if (
    !arrowTable ||
    !columns ||
    !viewParams?.segments ||
    !viewParams?.dateHierarchy
  ) {
    return null;
  }

  // Get unique dates efficiently using Arrow vectors directly
  const dateField = viewParams.dateHierarchy;
  const dateVector = arrowTable.getChild(dateField);
  if (!dateVector) {
    return null;
  }

  const dateSet = new Set<number>();
  for (let i = 0; i < dateVector.length; i++) {
    const dateValue = dateVector.get(i);
    if (dateValue != null) {
      dateSet.add(dayjs.utc(dateValue).valueOf());
    }
  }
  const allDates = Array.from(dateSet)
    .sort((a, b) => a - b)
    .map((ts) => dayjs.utc(ts));

  // Get all necessary vectors once
  const segmentVectors = viewParams.segments.map((segment: string) =>
    arrowTable.getChild(segment)
  );
  const measureVector = arrowTable.getChild("measure");
  const valueVector = arrowTable.getChild("value");
  const segmentationRankVector =
    arrowTable.getChild("segmentation_rank") ||
    arrowTable.getChild("segmentationRank");
  const measureRankVector =
    arrowTable.getChild("measure_rank") || arrowTable.getChild("metric_rank");

  if (
    !measureVector ||
    !valueVector ||
    segmentVectors.some((v: arrow.Vector | null) => !v)
  ) {
    return null;
  }

  // Build efficient lookup maps in one pass (O(n) upfront, O(1) lookup)
  const segMeasureToRows = new Map<string, number[]>();
  const segMeasureKeys: string[] = [];
  const segMeasureData = new Map<
    string,
    {
      segmentationKey: string;
      measure: string;
      segmentValues: string[];
      segmentationRank: number;
      measureRank: number;
    }
  >();

  // Single pass through Arrow data to build all lookup structures
  for (let i = 0; i < arrowTable.numRows; i++) {
    const segmentValues = segmentVectors.map((vector: arrow.Vector | null) =>
      String(vector!.get(i) || "")
    );
    const segmentationKey = segmentValues.join(" | ");
    const measure = String(measureVector.get(i) || "");
    const combinedKey = `${segmentationKey}||${measure}`;

    // Track arrow row indices for this segmentation+measure
    if (!segMeasureToRows.has(combinedKey)) {
      segMeasureToRows.set(combinedKey, []);
      segMeasureKeys.push(combinedKey);

      // Store metadata for this combination
      segMeasureData.set(combinedKey, {
        segmentationKey,
        measure,
        segmentValues,
        segmentationRank: Number(segmentationRankVector?.get(i) || 0),
        measureRank: Number(measureRankVector?.get(i) || 0),
      });
    }
    segMeasureToRows.get(combinedKey)!.push(i);
  }

  // Validate and fix segmentation rank consistency
  const segmentationRanks = new Map<string, number>();
  for (const [combinedKey, data] of segMeasureData.entries()) {
    const existingRank = segmentationRanks.get(data.segmentationKey);
    if (existingRank === undefined) {
      segmentationRanks.set(data.segmentationKey, data.segmentationRank);
    } else if (existingRank !== data.segmentationRank) {
      console.warn(
        `[SupplyWalkEdit] Inconsistent segmentation rank for "${data.segmentationKey}": ${existingRank} vs ${data.segmentationRank}`
      );
      // Use the first encountered rank for consistency
      data.segmentationRank = existingRank;
    }
  }

  // Sort combinations by ranks (one-time sort)
  const sortedKeys = segMeasureKeys.sort((keyA, keyB) => {
    const dataA = segMeasureData.get(keyA)!;
    const dataB = segMeasureData.get(keyB)!;

    // Primary sort: segmentation rank
    if (dataA.segmentationRank !== dataB.segmentationRank) {
      return dataA.segmentationRank - dataB.segmentationRank;
    }

    // Secondary sort: segmentation string (ensures stable grouping)
    if (dataA.segmentationKey !== dataB.segmentationKey) {
      return dataA.segmentationKey.localeCompare(dataB.segmentationKey);
    }

    // Tertiary sort: measure rank within same segmentation
    return dataA.measureRank - dataB.measureRank;
  });

  // Create efficient getRow function with O(1) lookups
  const getRow = (rowIndex: number): Record<string, any> => {
    if (rowIndex >= sortedKeys.length) return {}; // Return empty object instead of null

    const metadata = segMeasureData.get(sortedKeys[rowIndex])!;
    const arrowRowIndices = segMeasureToRows.get(sortedKeys[rowIndex])!;

    // Build date values efficiently
    const dateValues: Record<string, any> = {};
    arrowRowIndices.forEach((arrowRowIndex) => {
      const dateValue = dayjs.utc(dateVector.get(arrowRowIndex)).valueOf();
      const value = valueVector.get(arrowRowIndex);
      dateValues[dateValue] = value;
    });

    // Create the base row data with export compatibility properties
    const rowData: Record<string, any> = {
      segmentationKey: metadata.segmentationKey,
      segmentValues: metadata.segmentValues,
      measure: metadata.measure,
      dateValues,
      segmentationRank: metadata.segmentationRank,
      measureRank: metadata.measureRank,
      _arrowRowIndices: arrowRowIndices,

      // Export compatibility properties to match the pattern expected by export logic
      // For supply walk, treat each row as a "detail" row since there's no grouping
      type: "detail",
      groupKey: metadata.segmentationKey,

      // Direct column access for export
      segmentation: metadata.segmentationKey,
    };

    // Add date column values directly on the row object for export
    allDates.forEach((date) => {
      const dateKey = `${date.valueOf()}`;
      rowData[dateKey] = dateValues[date.valueOf()] || "";
    });

    return rowData;
  };

  // Extract actual SKU and location values for column header
  const firstRow = sortedKeys.length > 0 ? getRow(0) : null;
  const actualSegmentation = firstRow?.segmentationKey || "Unknown";

  // Build MUI columns - Modified for nested table
  const muiColumns = [
    {
      key: "measure",
      cellType: new arrow.Utf8(),
      headerType: new arrow.Utf8(),
      sticky: true,
      width: 200,
      label: actualSegmentation, // Show actual SKU | location values
      renderCell: (row: any, rowIndex: number) => {
        // Customize display for purchase order arrivals (open), (review), and (approved)
        let displayText = row.measure;
        let paddingLeft = 0;

        if (row.measure === "purchase order arrivals (open)") {
          displayText = "open";
          paddingLeft = 24; // Indent
        } else if (row.measure === "purchase order arrivals (review)") {
          displayText = "in review";
          paddingLeft = 24; // Indent
        } else if (row.measure === "purchase order arrivals (approved)") {
          displayText = "approved";
          paddingLeft = 24; // Indent
        }

        return (
          <Box
            sx={{
              height: "40px",
              display: "flex",
              alignItems: "center",
              p: 1,
              pl: paddingLeft > 0 ? `${paddingLeft}px` : 1,
            }}
          >
            {displayText}
          </Box>
        );
      },
    },
    ...allDates.map((date) => ({
      key: `${date.valueOf()}`,
      cellType: new arrow.Float64(),
      headerType: new arrow.Timestamp(arrow.TimeUnit.MILLISECOND),

      label: shortFormatDateString(date),
      renderCell: (row: any, rowIndex: number) => {
        const value = row.dateValues[date.valueOf()];
        const numericString = String(value ?? "").replace(/,/g, "");
        const numericValue = Number(numericString);

        const measureLower = row.measure.toLowerCase();
        let highlightColor = undefined;

        // Check for negative inventory - RED (highest priority)
        const isStartingInventory = measureLower === "starting inventory";
        const isEndingInventory = measureLower === "ending inventory";

        if (
          (isStartingInventory || isEndingInventory) &&
          numericString !== "" &&
          !isNaN(numericValue) &&
          numericValue < 0
        ) {
          highlightColor = theme.palette.atomic.red1; // Red background for negative inventory
        }
        // Check for specific PO cells - YELLOW (only if parentEdit exists)
        else if (
          parentEdit &&
          parentEdit.orderPlacedDate &&
          parentEdit.deliveryDate
        ) {
          // Get the month timestamps for PO placed and delivery dates
          const placedMonth = dayjs
            .utc(parentEdit.orderPlacedDate)
            .startOf("month")
            .valueOf();
          const deliveryMonth = dayjs
            .utc(parentEdit.deliveryDate)
            .startOf("month")
            .valueOf();
          const currentMonth = date.valueOf();

          // Highlight purchase order to place cell at the placed date
          if (
            measureLower === "purchase order to place" &&
            currentMonth === placedMonth
          ) {
            highlightColor = theme.palette.atomic?.orange1 || "#fff3cd"; // Yellow/orange background
          }
          // Highlight purchase order arrivals cell at the delivery date
          // Check for approved or in review based on approval status
          else if (measureLower.includes("purchase order arrivals")) {
            const isApproved = parentEdit.approved ?? false;
            const shouldHighlight =
              (isApproved &&
                measureLower === "purchase order arrivals (approved)" &&
                currentMonth === deliveryMonth) ||
              (!isApproved &&
                measureLower === "purchase order arrivals (review)" &&
                currentMonth === deliveryMonth);

            if (shouldHighlight) {
              highlightColor = theme.palette.atomic?.orange1 || "#fff3cd"; // Yellow/orange background
            }
          }
        }

        // Color code weeks of supply (only if not already highlighted)
        let cellColor = undefined;
        if (!highlightColor) {
          const isWOS =
            measureLower ===
              getWOSMeasureKey(
                "starting",
                viewParams?.dateHierarchy || ""
              ).toLowerCase() ||
            measureLower ===
              getWOSMeasureKey(
                "ending",
                viewParams?.dateHierarchy || ""
              ).toLowerCase() ||
            measureLower.includes("target weeks of supply");

          // Skip coloring for target weeks of supply
          const isTargetWOS = measureLower.includes("target weeks of supply");

          if (
            isWOS &&
            !isTargetWOS &&
            numericString !== "" &&
            !isNaN(numericValue)
          ) {
            // Find target WOS within the same segmentation
            let targetWOS = null;
            const targetMeasureName = getWOSMeasureKey(
              "target",
              viewParams?.dateHierarchy || ""
            );

            // Find target measure in current segmentation by looking at nearby rows
            for (
              let i = Math.max(0, rowIndex - 10);
              i <= Math.min(sortedKeys.length - 1, rowIndex + 10);
              i++
            ) {
              const checkRow = getRow(i);
              if (
                checkRow &&
                checkRow.segmentationKey &&
                checkRow.segmentationKey === row.segmentationKey &&
                checkRow.measure &&
                checkRow.measure.toLowerCase() ===
                  targetMeasureName.toLowerCase()
              ) {
                const targetValRaw = checkRow.dateValues[date.valueOf()];
                if (
                  targetValRaw !== undefined &&
                  targetValRaw !== null &&
                  targetValRaw !== ""
                ) {
                  const targetString =
                    typeof targetValRaw === "string"
                      ? targetValRaw.replace(/,/g, "")
                      : String(targetValRaw);
                  const parsedTarget = Number(targetString);
                  if (!isNaN(parsedTarget)) {
                    targetWOS = parsedTarget;
                  }
                }
                break;
              }
            }

            if (targetWOS !== null && !isNaN(targetWOS)) {
              cellColor = getSupplyCellBackgroundColor({
                value: numericValue,
                targetValue: targetWOS,
                theme,
              });
            }
          }
        }

        // Determine background color
        let backgroundColor = "white"; // Default white background
        if (highlightColor) {
          backgroundColor = highlightColor;
        } else if (cellColor) {
          backgroundColor = cellColor; // Use calculated color for weeks of supply
        }

        // Format the value with commas for numeric values
        const formattedValue = formatCellValue(value, new arrow.Float64());

        return (
          <Box
            sx={{
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end", // Right-align numerical values
              backgroundColor: backgroundColor,
              p: 1,
            }}
          >
            {formattedValue ?? ""}
          </Box>
        );
      },
    })),
  ];

  return {
    columns: muiColumns,
    getRow,
    rowCount: sortedKeys.length,
    arrowTable,
    columnTotals: {},
  };
}

// Global row edit state manager
const globalRowEditState = new Map<
  string,
  {
    isEditing: boolean;
    editValues: Record<string, string>;
    listeners: Set<() => void>;
  }
>();

// Row edit state manager
const getRowEditState = (
  rowKey: string,
  editableColumns: string[],
  row: any
) => {
  if (!globalRowEditState.has(rowKey)) {
    globalRowEditState.set(rowKey, {
      isEditing: false,
      editValues: editableColumns.reduce(
        (acc, col) => {
          acc[col] = String(row[col] || "");
          return acc;
        },
        {} as Record<string, string>
      ),
      listeners: new Set(),
    });
  }
  return globalRowEditState.get(rowKey)!;
};

// Notify all listeners for a row
const notifyRowListeners = (rowKey: string) => {
  const state = globalRowEditState.get(rowKey);
  if (state) {
    state.listeners.forEach((listener) => listener());
  }
};

// Row edit state hook
const useRowEditState = (
  rowKey: string,
  editableColumns: string[],
  row: any
) => {
  const [state, setState] = useState(() =>
    getRowEditState(rowKey, editableColumns, row)
  );

  useEffect(() => {
    const currentState = getRowEditState(rowKey, editableColumns, row);
    const listener = () => {
      setState({ ...currentState });
    };
    currentState.listeners.add(listener);

    return () => {
      currentState.listeners.delete(listener);
    };
  }, [rowKey, editableColumns, row]);

  const handleEdit = () => {
    const currentState = getRowEditState(rowKey, editableColumns, row);
    if (!currentState.isEditing) {
      // Initialize edit values with current row data
      currentState.editValues = editableColumns.reduce(
        (acc, col) => {
          acc[col] = String(row[col] || "");
          return acc;
        },
        {} as Record<string, string>
      );
    }
    currentState.isEditing = !currentState.isEditing;
    notifyRowListeners(rowKey);
  };

  const handleValueChange = (column: string, value: string) => {
    const currentState = getRowEditState(rowKey, editableColumns, row);
    currentState.editValues[column] = value;
    notifyRowListeners(rowKey);
  };

  return {
    isEditing: state.isEditing,
    editValues: state.editValues,
    handleEdit,
    handleValueChange,
  };
};

// Individual cell component for editable cells
const EditableCell = React.memo(
  ({
    row,
    column,
    editableColumns,
    isEditing,
    editValue,
    onValueChange,
  }: {
    row: any;
    column: any;
    editableColumns: string[];
    isEditing: boolean;
    editValue: string;
    onValueChange: (value: string) => void;
  }) => {
    if (isEditing) {
      return (
        <Box
          sx={{
            p: 1,
            height: "40px",
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <CompactTableTextField
            value={editValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onValueChange(e.target.value);
            }}
            sx={{ width: "100%", maxWidth: "150px" }}
          />
        </Box>
      );
    } else {
      // Use original column formatting when not editing
      const formattedValue = formatCellValue(row[column.key], column.cellType);
      return (
        <Box
          sx={{
            p: 1,
            height: "40px",
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          {formattedValue}
        </Box>
      );
    }
  }
);

// Edit button component
const EditButton = React.memo(
  ({
    row,
    editableColumns,
    isEditing,
    onToggleEdit,
  }: {
    row: any;
    editableColumns: string[];
    isEditing: boolean;
    onToggleEdit: () => void;
  }) => {
    return (
      <IconButton size="small" onClick={onToggleEdit} sx={{ p: 0.5 }}>
        {isEditing ? (
          <CheckIcon fontSize="small" />
        ) : (
          <EditIcon fontSize="small" />
        )}
      </IconButton>
    );
  }
);

// Wrapper components that use the hook
const EditableCellWrapper = React.memo(
  ({
    row,
    column,
    editableColumns,
    rowIndex,
  }: {
    row: any;
    column: any;
    editableColumns: string[];
    rowIndex: number;
  }) => {
    const rowKey = `row-${rowIndex}`;
    const { isEditing, editValues, handleValueChange } = useRowEditState(
      rowKey,
      editableColumns,
      row
    );

    return (
      <EditableCell
        row={row}
        column={column}
        editableColumns={editableColumns}
        isEditing={isEditing}
        editValue={editValues[column.key]}
        onValueChange={(value: string) => handleValueChange(column.key, value)}
      />
    );
  }
);

const EditButtonWrapper = React.memo(
  ({
    row,
    editableColumns,
    rowIndex,
  }: {
    row: any;
    editableColumns: string[];
    rowIndex: number;
  }) => {
    const rowKey = `row-${rowIndex}`;
    const { isEditing, handleEdit } = useRowEditState(
      rowKey,
      editableColumns,
      row
    );

    return (
      <EditButton
        row={row}
        editableColumns={editableColumns}
        isEditing={isEditing}
        onToggleEdit={handleEdit}
      />
    );
  }
);

// --- PO EDIT DATA (Copy of getFlatTableData for parent table) ---
export function getPOEditData({
  arrowTable,
  columns,
  sortConfig = [],
  onSort,
  columnSchema,
  setSortConfig,
  firstMount,
}: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  sortConfig?: Array<{ key: string; direction: "asc" | "desc" }>;
  onSort?: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  setSortConfig?: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  firstMount?: React.MutableRefObject<boolean>;
}) {
  const numRows = arrowTable.numRows;

  // Use direct Arrow table sorting - no array conversion needed
  let visibleRows = Array.from({ length: numRows }, (_, i) => i);
  if (sortConfig && sortConfig.length > 0) {
    visibleRows = getSortedRowIndices(arrowTable, columns, sortConfig);
  }

  const muiColumns = buildMUIColumns({
    columns,
    sortConfig,
    onSort,
    columnSchema,
  });
  const getRow = makeGetRowFromArrowTableWithIndices(
    arrowTable,
    columns,
    visibleRows
  );

  // Calculate totals using the reusable function
  const columnTotals = calculateColumnTotals({
    arrowTable,
    columns: muiColumns,
    hasTableId: false,
  });

  // Define editable columns
  const editableColumns = ["quantity", "cost", "po_week", "eta_week"];

  // Enhanced columns with edit functionality
  const enhancedColumns = muiColumns.map((column) => {
    if (editableColumns.includes(column.key)) {
      return {
        ...column,
        renderCell: (row: any, rowIndex: number) => {
          return (
            <EditableCellWrapper
              row={row}
              column={column}
              editableColumns={editableColumns}
              rowIndex={rowIndex}
            />
          );
        },
      };
    }
    return column;
  });

  // Add edit column at the end
  const columnsWithEdit = [
    ...enhancedColumns,
    {
      key: "__edit",
      label: "",
      width: 50,
      align: "center" as const,
      sticky: false,
      renderCell: (row: any, rowIndex: number) => {
        return (
          <EditButtonWrapper
            row={row}
            editableColumns={editableColumns}
            rowIndex={rowIndex}
          />
        );
      },
    },
  ];

  return {
    columns: columnsWithEdit,
    getRow,
    rowCount: visibleRows.length,
    arrowTable,
    columnTotals,
  };
}
